import { BuilderActions } from '../../../shared'
import { IBuilderComponent } from '../../base'
import {
  formatColorFormat,
  formatCornerRadiusFormat,
  formatFromVariableSelect,
  formatFromVariableText,
  formatHorizontalAlignmentFormat,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  createResponsiveValue,
  deepMerge,
  parseAlignmentFormat,
  parseColorFormat,
  parseCornerRadiusFormat,
  parseSpacingFormat,
  parseToVariableSelect,
  parseToVariableText,
  parseTypographyFormat,
  parseWidthFormat,
} from '../../parser-utils'
import type {
  ColorAdaptiveValue,
  ColorType,
  ColorVariant,
  CornerRadiusValue,
  GradientDirection,
  HorizontalAlignment,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import buttonTemplate from './button.template.json'

export interface IButtonComponentSettings {
  // Data settings
  'button-data-label': VariableTextValue
  'button-data-icon': VariableSelectValue
  'button-data-badge': VariableTextValue

  // Design settings
  'button-design-color-variant'?: ColorAdaptiveValue<ColorVariant>
  'button-design-group-color'?: ColorAdaptiveValue<ColorVariant>
  'button-design-color-type'?: ColorAdaptiveValue<ColorType>
  'button-design-color-type-solid'?: ColorAdaptiveValue<ColorVariant>
  'button-design-color-type-start-gradient'?: ColorAdaptiveValue<ColorVariant>
  'button-design-color-type-end-gradient': ColorAdaptiveValue<ColorVariant>
  'button-design-color-type-gradient-direction': ColorAdaptiveValue<GradientDirection>
  'button-design-badge-background-color-type'?: string
  'button-design-badge-background-color-solid'?: string
  'button-design-badge-background-color-start-gradient'?: string
  'button-design-badge-background-color-end-gradient'?: string
  'button-design-badge-background-color-gradient-direction'?: string
  'button-design-badge-color'?: any
  'button-design-badge-color-solid'?: ColorAdaptiveValue<ColorVariant>
  'button-design-badge-color-start-gradient'?: ColorAdaptiveValue<ColorVariant>
  'button-design-badge-color-end-gradient'?: ColorAdaptiveValue<ColorVariant>
  'button-design-badge-color-gradient-direction'?: ColorAdaptiveValue<ColorVariant>
  'button-design-icon-color'?: any
  'button-design-icon-color-solid'?: ColorAdaptiveValue<ColorVariant>
  'button-design-icon-position'?: number | string
  'button-design-shape'?: CornerRadiusValue
  'button-design-fit'?: boolean
  'button-design-width'?: ResponsiveValue<string[] | number[]>
  'button-design-full-width': OverflowSettings<ResponsiveValue<boolean>>
  'button-design-size'?: string
  'button-design-h-align'?: ResponsiveValue<HorizontalAlignment>
  'button-design-padding'?: ResponsiveValue<SpacingInputValue>
  'button-design-margin'?: ResponsiveValue<SpacingInputValue>
  'button-design-badge-padding'?: SpacingInputValue
  'button-design-badge-top-position'?: number | string
  'button-design-badge-right-position'?: number | string
  'button-design-typography'?: ResponsiveValue<TypographyValue>
  'button-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'

  // Actions
  'button-action-click': BuilderActions
}

export interface IButtonComponent extends IBuilderComponent {
  componentId: 'button'
  groupId: 'button'
  settings: IButtonComponentSettings
}

export interface AiButtonInput {
  label: string[] // Format: ["text:Submit"] or ["text:Hello ", "profile:username"]
  icon?: string // Format: "icon:add" or "icon:home"
  badge?: string[] // Format: ["text:3"] or ["text:New"]
  backgroundColor?: string // Format: "solid:primary-40" or "gradient:primary-40|primary-80:to-right"
  badgeColor?: string // Format: "solid:primary-40" or "gradient:primary-40|primary-80:to-right"
  typography?: string // Format: "label-medium" or "label-medium ta:label-large"
  align?: string // Format: "h:center" or "h:start ta:h:center"
  padding?: string // Format: "p:16px" or "pt:20px pb:20px" or "p:16px ta:p:32px"
  margin?: string // Format: "m:2rem" or "mb:16px" or "m:2rem ta:m:3rem"
  cornerRadius?: string // Format: "medium" or "small ta:medium de:large"
  size?: string // Format: "md" or "sm ta:md de:lg"
  width?: string // Format: "w:100%" or "w:300px" or "w:100% de:w:800px"
  fullWidth?: boolean
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
  clickAction?: BuilderActions[]
}

const BUTTON_DEFAULT_TEMPLATE = buttonTemplate as unknown as IButtonComponentSettings

export class ButtonComp {
  getTemplate(): IButtonComponentSettings {
    return BUTTON_DEFAULT_TEMPLATE
  }

  /**
   * format button component settings to AI button input
   */
  format(settings: IButtonComponentSettings): AiButtonInput {
    const template = this.getTemplate()
    const result: AiButtonInput = {
      label: formatFromVariableText(settings['button-data-label']),
    }

    // Icon
    if (settings['button-data-icon']) {
      const icon = formatFromVariableSelect(settings['button-data-icon'])
      if (icon) {
        result.icon = icon
      }
    }

    // Badge
    if (settings['button-data-badge']) {
      const badge = formatFromVariableText(settings['button-data-badge'])
      if (badge.length > 0 && badge[0] !== 'text:') {
        result.badge = badge
      }
    }

    // Background color
    const backgroundColor = formatColorFormat({
      colorType: settings['button-design-color-type'],
      colorSolid: settings['button-design-color-type-solid'],
      colorStartGradient: settings['button-design-color-type-start-gradient'],
      colorEndGradient: settings['button-design-color-type-end-gradient'],
      colorGradientDirection: settings['button-design-color-type-gradient-direction'],
    })
    if (backgroundColor) {
      // Only include if it's different from template default
      // Template default is 'adaptive', so we skip outputting 'adaptive' unless template was different
      const templateColorType = template['button-design-color-type']?.light || 'adaptive'
      if (backgroundColor !== 'adaptive' || templateColorType !== 'adaptive') {
        result.backgroundColor = backgroundColor
      }
    }

    // Badge color
    const badgeColor = formatColorFormat({
      colorType: { light: 'solid', dark: 'solid' }, // Badge color doesn't have type, assume solid
      colorSolid: settings['button-design-badge-color-solid'],
      colorStartGradient: settings['button-design-badge-color-start-gradient'],
      colorEndGradient: settings['button-design-badge-color-end-gradient'],
      colorGradientDirection: settings['button-design-badge-color-gradient-direction'] as any, // Type mismatch in settings
    })
    if (badgeColor) {
      result.badgeColor = badgeColor
    }

    // Typography
    const typography = formatTypographyFormat(settings['button-design-typography'])
    if (typography) {
      result.typography = typography
    }

    // Alignment - button uses horizontal alignment (h:)
    const alignment = formatHorizontalAlignmentFormat(settings['button-design-h-align'])
    if (alignment) {
      result.align = alignment
    }

    // Padding
    const padding = formatSpacingFormat(settings['button-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Margin
    const margin = formatSpacingFormat(settings['button-design-margin'])
    if (margin) {
      result.margin = margin
    }

    // Corner radius
    const cornerRadius = formatCornerRadiusFormat(settings['button-design-shape'])
    if (cornerRadius) {
      result.cornerRadius = cornerRadius
    }

    // Size
    if (settings['button-design-size'] && settings['button-design-size'] !== template['button-design-size']) {
      result.size = settings['button-design-size']
    }

    // Width
    const width = formatWidthFormat(settings['button-design-width'])
    if (width) {
      result.width = width
    }

    // Full width
    if (settings['button-design-full-width']) {
      const fullWidth = settings['button-design-full-width'].phone
      if (fullWidth !== template['button-design-full-width']?.phone) {
        result.fullWidth = fullWidth
      }
    }

    // Hover Effect
    if (settings['button-design-hover-effect']) {
      const hoverEffect = settings['button-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    // Actions (copied as-is)
    if (settings['button-action-click']) {
      result.clickAction = settings['button-action-click'] as any
    }

    return result
  }

  /**
   * parse AI button input to button component settings
   */
  parse(input: AiButtonInput): IButtonComponentSettings {
    const result = deepMerge(BUTTON_DEFAULT_TEMPLATE, {})

    if (input.label) {
      result['button-data-label'] = parseToVariableText(input.label)
    }

    if (input.icon) {
      result['button-data-icon'] = parseToVariableSelect(input.icon)
    }

    if (input.badge) {
      result['button-data-badge'] = parseToVariableText(input.badge)
    }

    if (input.backgroundColor) {
      // Parse color format (supports: "adaptive", "solid:primary-40", "gradient:primary-40|primary-80:to-right")
      const colorParsed = parseColorFormat(input.backgroundColor)
      result['button-design-color-type'] = colorParsed.colorType
      result['button-design-color-type-solid'] = colorParsed.colorSolid
      result['button-design-color-type-start-gradient'] = colorParsed.colorStartGradient
      result['button-design-color-type-end-gradient'] = colorParsed.colorEndGradient
      result['button-design-color-type-gradient-direction'] = colorParsed.colorGradientDirection
      result['button-design-color-variant'] = colorParsed.colorSolid
      result['button-design-group-color'] = colorParsed.colorSolid
    }

    if (input.badgeColor) {
      const colorParsed = parseColorFormat(input.badgeColor)
      result['button-design-badge-color-solid'] = colorParsed.colorSolid
      result['button-design-badge-color-start-gradient'] = colorParsed.colorStartGradient
      result['button-design-badge-color-end-gradient'] = colorParsed.colorEndGradient
      result['button-design-badge-color-gradient-direction'] =
        colorParsed.colorGradientDirection as unknown as ColorAdaptiveValue<ColorVariant>
    }

    if (input.typography) {
      result['button-design-typography'] = parseTypographyFormat(input.typography)
    }

    if (input.align) {
      const alignmentParsed = parseAlignmentFormat(input.align)
      if (alignmentParsed.hAlign) {
        result['button-design-h-align'] = alignmentParsed.hAlign
      }
    }

    if (input.padding) {
      result['button-design-padding'] = parseSpacingFormat(input.padding)
    }

    if (input.margin) {
      result['button-design-margin'] = parseSpacingFormat(input.margin)
    }

    if (input.cornerRadius) {
      const cornerRadiusParsed = parseCornerRadiusFormat(input.cornerRadius)
      // Shape is stored as a single string, not responsive
      result['button-design-shape'] = cornerRadiusParsed.phone
    }

    if (input.size) {
      // Size is stored as a single string, not responsive
      // For now, just use the first value or default
      const sizeValue = input.size.split(/\s+/)[0]
      result['button-design-size'] = sizeValue || 'md'
    }

    if (input.width) {
      result['button-design-width'] = parseWidthFormat(input.width)
    }

    if (input.fullWidth !== undefined) {
      result['button-design-full-width'] = {
        ...createResponsiveValue(input.fullWidth),
        overflowSetting: createResponsiveValue(true),
      }
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['button-design-hover-effect'] = input.hoverEffect
    }

    if (input.clickAction) {
      // @ts-ignore
      result['button-action-click'] = input.clickAction
    }

    return result
  }
}
